import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { apiClient } from "../../services/api";
import { useConfirm } from "../../hooks/useConfirm";
import RefreshButton from "../RefreshButton/RefreshButton";
import PageModal from "./PageModal";
import "./Notebook.css";

const API_BASE = import.meta.env.VITE_API_URL || "/api";

// Debounce helper for autosave
function useDebouncedCallback(fn, delay = 500) {
  const ref = useRef(null);
  return useCallback((...args) => {
    if (ref.current) clearTimeout(ref.current);
    ref.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

function Notebook() {
  const [notebooks, setNotebooks] = useState([]);
  const [pages, setPages] = useState([]); // all pages across all notebooks
  const [activeNotebookId, setActiveNotebookId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, confirmDialog] = useConfirm();

  const fetchAllPages = useCallback(async (nbs) => {
    try {
      const results = await Promise.all(
        nbs.map((nb) => apiClient.get(`/pages?notebookId=${encodeURIComponent(nb.id)}`))
      );
      const allPages = results.flatMap((data) => data.items || []);
      allPages.sort((a, b) =>
        (a.order ?? 0) - (b.order ?? 0) || (a.createdAt || "").localeCompare(b.createdAt || "")
      );
      setPages(allPages);
    } catch (err) {
      console.error("Failed to fetch all pages:", err);
    }
  }, []);

  const fetchNotebooks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get("/notebooks");
      const nbs = (data.items || []).sort((a, b) =>
        (a.order ?? 0) - (b.order ?? 0) || (a.name || "").localeCompare(b.name || "")
      );
      setNotebooks(nbs);
      setActiveNotebookId((prev) => prev || (nbs.length > 0 ? nbs[0].id : null));
      await fetchAllPages(nbs);
    } catch (err) {
      console.error("Failed to fetch notebooks:", err);
    } finally {
      setLoading(false);
    }
  }, [fetchAllPages]);

  const fetchPages = useCallback(async (notebookId) => {
    if (!notebookId) { return; }
    try {
      const data = await apiClient.get(`/pages?notebookId=${encodeURIComponent(notebookId)}`);
      const ps = (data.items || []).sort((a, b) =>
        (a.order ?? 0) - (b.order ?? 0) || (a.createdAt || "").localeCompare(b.createdAt || "")
      );
      // Merge into state (replace pages for this notebook, keep others)
      setPages((prev) => [
        ...prev.filter((p) => p.notebookId !== notebookId),
        ...ps,
      ]);
    } catch (err) {
      console.error("Failed to fetch pages:", err);
    }
  }, []);

  useEffect(() => { fetchNotebooks(); }, [fetchNotebooks]);

  // Auto-refresh when the tab/window regains visibility (catches backend changes from Anna)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        fetchNotebooks();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchNotebooks]);

  // Auto-refresh when Anna modifies a page (from chat or floating widget)
  useEffect(() => {
    const onPageUpdated = () => { fetchNotebooks(); };
    window.addEventListener("anna-page-updated", onPageUpdated);
    return () => window.removeEventListener("anna-page-updated", onPageUpdated);
  }, [fetchNotebooks]);

  const refreshAll = useCallback(async () => {
    await fetchNotebooks();
  }, [fetchNotebooks]);

  // --- Notebook CRUD ---
  const handleCreateNotebook = async () => {
    const name = window.prompt("New notebook name:");
    if (!name || !name.trim()) return;
    try {
      const res = await apiClient.post("/notebooks", { name: name.trim() });
      await fetchNotebooks();
      if (res?.item?.id) setActiveNotebookId(res.item.id);
    } catch (err) { console.error(err); }
  };

  const handleRenameNotebook = async (nb) => {
    const name = window.prompt("Rename notebook:", nb.name);
    if (!name || !name.trim() || name.trim() === nb.name) return;
    try {
      await apiClient.put(`/notebooks/${nb.id}`, { name: name.trim() });
      await fetchNotebooks();
    } catch (err) { console.error(err); }
  };

  const handleDeleteNotebook = async (nb) => {
    const hasPages = pages.length > 0 && nb.id === activeNotebookId;
    const msg = hasPages
      ? `Delete notebook "${nb.name}" and all ${pages.length} of its pages?`
      : `Delete notebook "${nb.name}"?`;
    if (!(await confirm(msg))) return;
    try {
      // First wipe pages (server requires explicit cascade only for action, REST cascades manually)
      const pageList = await apiClient.get(`/pages?notebookId=${encodeURIComponent(nb.id)}`);
      for (const p of (pageList.items || [])) {
        await apiClient.delete(`/pages/${p.id}`);
      }
      await apiClient.delete(`/notebooks/${nb.id}`);
      const remaining = notebooks.filter((n) => n.id !== nb.id);
      setActiveNotebookId(remaining[0]?.id ?? null);
      await fetchNotebooks();
    } catch (err) { console.error(err); }
  };

  // --- Page CRUD ---
  const handleCreatePage = useCallback(async (notebookId) => {
    const nbId = notebookId || activeNotebookId;
    if (!nbId) return;
    const title = window.prompt("New page title:");
    if (!title || !title.trim()) return;
    try {
      await apiClient.post("/pages", {
        notebookId: nbId,
        title: title.trim(),
        content: "",
        order: pages.filter((p) => p.notebookId === nbId).length,
      });
      await fetchPages(nbId);
    } catch (err) { console.error(err); }
  }, [activeNotebookId, pages, fetchPages]);

  const savePage = useCallback(async (pageId, patch) => {
    try {
      await apiClient.put(`/pages/${pageId}`, patch);
    } catch (err) {
      console.error("Save page failed:", err);
    }
  }, []);

  const debouncedSave = useDebouncedCallback(savePage, 500);

  // Optimistic-update helper used by the open book whenever any field on a
  // page changes. We update local state immediately and debounce the PUT.
  const updatePageLocal = useCallback((pageId, patch) => {
    setPages((prev) => prev.map((p) => (p.id === pageId ? { ...p, ...patch } : p)));
    debouncedSave(pageId, patch);
  }, [debouncedSave]);

  const handleDeletePage = useCallback(async (pageId) => {
    if (!(await confirm("Delete this page?"))) return;
    try {
      await apiClient.delete(`/pages/${pageId}`);
      setPages((prev) => prev.filter((p) => p.id !== pageId));
    } catch (err) { console.error(err); }
  }, [confirm]);

  const handleRenamePage = useCallback(async (page) => {
    const title = window.prompt("Rename page:", page.title);
    if (!title || !title.trim() || title.trim() === page.title) return;
    updatePageLocal(page.id, { title: title.trim() });
  }, [updatePageLocal]);

  // --- File attachment upload ---
  const fileInputRef = useRef(null);
  const handleAttachFile = useCallback(() => {
    const vp = pages.filter((p) => !p.archived);
    if (!vp.length) return;
    fileInputRef.current?.click();
  }, [pages]);

  const handleFileSelected = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = ""; // reset so same file can be re-selected

    // Attach to the first visible page (user can choose section via Anna)
    const vp = pages.filter((p) => !p.archived);
    const targetPage = vp[0];
    if (!targetPage) return;

    const token = localStorage.getItem("anna_token");
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    // 1. Get presigned upload URLs
    const uploadReq = files.map((f) => ({
      filename: f.name,
      contentType: f.type || "application/octet-stream",
      size: f.size,
    }));

    let uploadData;
    try {
      const resp = await fetch(`${API_BASE}/notebook-uploads`, {
        method: "POST",
        headers,
        body: JSON.stringify({ pageId: targetPage.id, files: uploadReq }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      uploadData = await resp.json();
    } catch (err) {
      console.error("Failed to get upload URLs:", err);
      return;
    }

    // 2. Upload each file to S3
    const uploaded = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const meta = uploadData.uploads[i];
      try {
        const putResp = await fetch(meta.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        });
        if (!putResp.ok) {
          console.error(`S3 PUT failed for ${file.name}: HTTP ${putResp.status}`);
          continue;
        }
        uploaded.push(meta);
      } catch (err) {
        console.error(`Upload failed for ${file.name}:`, err);
      }
    }

    // 3. Attach each uploaded file to the page content + metadata
    for (const meta of uploaded) {
      const attId = meta.id;
      const refLine = `📎 [${meta.filename}](attachment:${attId})`;
      const attachment = {
        id: attId,
        filename: meta.filename,
        contentType: meta.contentType,
        s3Key: meta.s3Key,
        section: "",
        size: meta.size || 0,
        uploadedAt: new Date().toISOString(),
      };

      // Update page content (append to end)
      const content = targetPage.content || "";
      const sep = content && !content.endsWith("\n") ? "\n" : "";
      const newContent = content + sep + `- ${refLine}\n`;

      // Update attachments array
      const attachments = [...(targetPage.attachments || []), attachment];
      updatePageLocal(targetPage.id, { content: newContent, attachments });
      // Keep local ref in sync for subsequent iterations
      targetPage.content = newContent;
      targetPage.attachments = attachments;
    }
  }, [pages, updatePageLocal]);

  const activeNotebook = useMemo(
    () => notebooks.find((n) => n.id === activeNotebookId) || null,
    [notebooks, activeNotebookId]
  );

  // Visible (non-archived) pages in order
  const visiblePages = useMemo(
    () => pages.filter((p) => !p.archived),
    [pages]
  );

  // Page detail modal state
  const [openPageId, setOpenPageId] = useState(null);
  const openPage = useMemo(
    () => (openPageId ? pages.find((p) => p.id === openPageId) : null),
    [openPageId, pages]
  );

  const handlePageSave = useCallback((pageId, newContent) => {
    updatePageLocal(pageId, { content: newContent });
  }, [updatePageLocal]);

  // Group pages by notebook for the card grid
  const notebookCards = useMemo(() => {
    return notebooks.map((nb) => ({
      ...nb,
      pages: pages.filter((p) => p.notebookId === nb.id && !p.archived)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.createdAt || "").localeCompare(b.createdAt || "")),
    }));
  }, [notebooks, pages]);

  if (loading && notebooks.length === 0) {
    return <div className="notebook-empty">📓 Loading notebooks…</div>;
  }

  return (
    <div className="notebook-page">
      {confirmDialog}

      <div className="notebook-toolbar">
        <div className="notebook-toolbar-left">
          <h2 className="notebook-title">📓 Notebooks</h2>
        </div>
        <div className="notebook-actions">
          <button className="btn-primary" onClick={handleCreateNotebook}>+ Notebook</button>
          <RefreshButton onClick={refreshAll} title="Refresh" />
        </div>
      </div>

      {notebooks.length === 0 ? (
        <div className="notebook-empty-state">
          <div className="empty-book">📕</div>
          <h3>Open your first notebook</h3>
          <p>Notebooks group related pages — the perfect place for room measurements, wifi credentials, ideas to come back to…</p>
          <button className="btn-primary" onClick={handleCreateNotebook}>+ Create notebook</button>
        </div>
      ) : (
        <div className="notebook-grid">
          {notebookCards.map((nb) => (
            <div key={nb.id} className="notebook-card">
              <div className="notebook-card-header">
                <div className="notebook-card-identity">
                  <span className="notebook-card-icon">{nb.icon || "📓"}</span>
                  <span className="notebook-card-name">{nb.name}</span>
                </div>
                <div className="notebook-card-actions">
                  <button className="icon-btn-sm" title="Add page" onClick={() => handleCreatePage(nb.id)}>+</button>
                  <button className="icon-btn-sm" title="Rename" onClick={() => handleRenameNotebook(nb)}>✏️</button>
                  <button className="icon-btn-sm danger" title="Delete" onClick={() => handleDeleteNotebook(nb)}>🗑️</button>
                </div>
              </div>

              <div className="notebook-card-pages">
                {nb.pages.length === 0 ? (
                  <p className="notebook-card-empty">No pages yet</p>
                ) : (
                  nb.pages.map((page) => (
                    <button
                      key={page.id}
                      className="page-item-btn"
                      onClick={() => setOpenPageId(page.id)}
                    >
                      <span className="page-item-title">{page.title || "Untitled"}</span>
                      <span className="page-item-meta">
                        {(page.attachments || []).length > 0 && <span className="page-att-badge">📎 {page.attachments.length}</span>}
                      </span>
                    </button>
                  ))
                )}
              </div>

              <div className="notebook-card-footer">
                <span className="notebook-card-count">{nb.pages.length} page{nb.pages.length !== 1 ? "s" : ""}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Page detail modal */}
      {openPage && (
        <PageModal
          page={openPage}
          onClose={() => setOpenPageId(null)}
          onSave={handlePageSave}
        />
      )}

      {/* Hidden file input for uploads */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />
    </div>
  );
}

export default Notebook;
